import { Icon } from '@clientbase/clientbase-library'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useAuthContext } from 'context/AuthContext'

import MerchantBadge from 'components/MerchantBadge'

import { theme } from 'styles/theme'

import * as S from './styles'

type SwitchTarget = { merchantUuid: string } | { merchantGroupUuid: string }

// "Grupo Enicomp · 2 empresas"; sem a contagem quando ela não veio
export const groupSubtitle = (name: string, count?: number) =>
  count ? `${name} · ${count} ${count === 1 ? 'empresa' : 'empresas'}` : name

/** Mostra o seletor só para quem tem mais de uma empresa ou algum grupo. */
export const useShowAccountSwitcher = () => {
  const { accounts, groups } = useAuthContext()
  return accounts.length > 1 || groups.length > 0
}

type AccountSwitcherListProps = {
  // Chamado ao clicar no contexto atual (não há troca a fazer)
  onClose: () => void
}

/**
 * SC-10999: lista de troca de contexto, com os grupos ("Multi-empresas") e as
 * empresas do login ("Suas contas"). Usada no menu da conta e no seletor do
 * topo do menu lateral.
 */
const AccountSwitcherList = ({ onClose }: AccountSwitcherListProps) => {
  const { user, accounts, groups, groupSession, switchAccount, switchingTo } =
    useAuthContext()

  const handleSwitch = async (target: SwitchTarget, isCurrent: boolean) => {
    if (isCurrent) {
      onClose()
      return
    }

    await switchAccount(target)
  }

  const renderSwitchStatus = (key: string, isCurrent: boolean) => {
    if (switchingTo === key) return <CircularProgress size={16} />
    if (isCurrent) {
      return <Icon icon="check" colorSX={theme.palette.primary.main} />
    }
    return null
  }

  return (
    <S.AccountsList>
      {groups.length > 0 && (
        <>
          <S.SectionLabel>Multi-empresas</S.SectionLabel>
          {groups.map((group) => {
            const isCurrent =
              groupSession?.merchant_group_uuid === group.merchant_group_uuid

            return (
              <S.SwitchItem
                key={group.merchant_group_uuid}
                selected={isCurrent}
                disabled={!!switchingTo}
                onClick={() =>
                  handleSwitch(
                    { merchantGroupUuid: group.merchant_group_uuid },
                    isCurrent
                  )
                }
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  minWidth={0}
                >
                  <S.GroupIconWrapper size={32}>
                    <GridViewRoundedIcon sx={{ fontSize: 18 }} />
                  </S.GroupIconWrapper>
                  <Stack minWidth={0}>
                    <Typography
                      variant={isCurrent ? 'smBold' : 'smLight'}
                      noWrap
                    >
                      Todas as empresas
                    </Typography>
                    <Typography
                      variant="xsLight"
                      color={theme.palette.neutral[400]}
                      noWrap
                    >
                      {groupSubtitle(group.name, group.merchant_uuids?.length)}
                    </Typography>
                  </Stack>
                </Stack>
                {renderSwitchStatus(group.merchant_group_uuid, isCurrent)}
              </S.SwitchItem>
            )
          })}
          <S.SectionDivider />
        </>
      )}

      <S.SectionLabel>Suas contas</S.SectionLabel>
      {accounts.map((account) => {
        const isCurrent = !groupSession && account.merchant_uuid === user?.uuid

        return (
          <S.SwitchItem
            key={account.merchant_uuid}
            selected={isCurrent}
            disabled={!!switchingTo}
            onClick={() =>
              handleSwitch({ merchantUuid: account.merchant_uuid }, isCurrent)
            }
          >
            <MerchantBadge
              name={account.name}
              logoUrl={account.logo_url}
              size={32}
              variant={isCurrent ? 'smBold' : 'smLight'}
            />
            {renderSwitchStatus(account.merchant_uuid, isCurrent)}
          </S.SwitchItem>
        )
      })}
    </S.AccountsList>
  )
}

export default AccountSwitcherList
